import React, { useEffect, useState } from 'react';
import { X, Loader2, CalendarCheck } from 'lucide-react';
import { format, addDays } from 'date-fns';
import { api } from '../../lib/api';

// Book the first visit on an accepted referral from OUTSIDE the clinic —
// the firm on the patient's behalf. Pulls the clinic's providers, visit
// types and real open slots through the referral, and books on the spot
// (the clinic already accepted this patient).
const inputClass = 'w-full px-3.5 py-[11px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] font-[inherit] transition-all focus:outline-none focus:border-[#0DA7CA] focus:shadow-[0_0_0_3px_rgba(13,167,202,0.15)]';
const SPECIALTY_HINT = { chiro: /chiro/i, imaging: /mri|imag|scan|x-ray/i, ortho: /ortho/i, pt: /physical|therap|rehab/i, neuro: /neuro|concuss/i };

export default function ReferralBookingModal({ orgId, referral, onClose, onBooked }) {
  const [options, setOptions] = useState(null);
  const [type, setType] = useState(null);
  const [providerId, setProviderId] = useState('');
  const [date, setDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slot, setSlot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const minDate = format(addDays(new Date(), 1), 'yyyy-MM-dd');
  const patientName = referral.client_snapshot?.name || 'the patient';

  useEffect(() => {
    api.getReferralBookingOptions(orgId, referral.id)
      .then((o) => {
        setOptions(o);
        setProviderId(o.providers?.[0]?.id || '');
        const hint = SPECIALTY_HINT[o.specialty];
        setType((o.types || []).find((t) => hint && hint.test(`${t.label} ${t.key}`)) || (o.types || []).find((t) => /consult|new patient|eval/i.test(t.label)) || o.types?.[0] || null);
      })
      .catch((e) => setError(e.message));
  }, [orgId, referral.id]);

  useEffect(() => {
    if (!providerId || !date) { setSlots([]); return; }
    setSlotsLoading(true); setSlot(null);
    api.getReferralAvailability(orgId, referral.id, { provider_id: providerId, date, duration: 30, tz_offset: new Date().getTimezoneOffset() })
      .then((s) => setSlots(s || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [orgId, referral.id, providerId, date]);

  const book = async () => {
    if (!slot) return;
    setSubmitting(true); setError('');
    try {
      await api.bookReferralVisit(orgId, referral.id, {
        provider_id: providerId || null,
        starts_at: slot.starts_at, ends_at: slot.ends_at,
        type: type?.key || 'consultation',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
      });
      onBooked?.();
    } catch (e) {
      setError(e.message || 'Could not book');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-overlay backdrop-blur-lg flex items-center justify-center z-[200] animate-fade-in p-4" onClick={onClose}>
      <div className="w-full max-w-[480px] max-h-[90vh] bg-elevated border border-theme rounded-xl shadow-2xl animate-modal-in overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 sm:py-5 border-b border-theme flex-shrink-0">
          <h3 className="text-[17px] sm:text-[18px] font-bold tracking-tight text-primary-c">Book {patientName}'s first visit</h3>
          <button onClick={onClose} className="w-9 h-9 rounded-lg bg-tertiary text-muted-c flex items-center justify-center hover:bg-hover-surface hover:text-primary-c transition-all"><X size={14} strokeWidth={2.2} /></button>
        </div>
        <div className="px-5 sm:px-6 py-5 sm:py-6 space-y-5 overflow-y-auto">
          <div className="bg-tertiary rounded-[10px] p-3.5 border border-theme">
            <div className="text-[10px] font-bold uppercase tracking-[1px] text-muted-c mb-1">Clinic</div>
            <div className="text-[13px] font-semibold text-primary-c">{options?.clinic?.name || referral.provider?.name}</div>
            {options?.clinic?.location && <div className="text-[11px] text-muted-c mt-0.5">{[options.clinic.location.name, options.clinic.location.address_line1, options.clinic.location.city, options.clinic.location.state].filter(Boolean).join(', ')}</div>}
            <div className="text-[11px] text-muted-c mt-0.5">Accepted the referral{referral.service ? ` · ${referral.service}` : ''}. Booking here is on the spot — the patient gets the confirmation by email and text.</div>
          </div>

          {!options && !error && <p className="text-[12px] text-muted-c flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Loading the clinic's calendar…</p>}

          {options && (
            <>
              <div>
                <label className="block text-[12px] font-semibold text-secondary-c mb-2">Visit type</label>
                {options.types.length === 0 ? (
                  <p className="text-[12px] text-muted-c">The clinic has no visit types set up — a consultation will be booked.</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {options.types.map((t) => {
                      const isSel = type?.key === t.key;
                      return (
                        <button key={t.key} onClick={() => setType(t)} className={`px-3.5 py-2 rounded-lg text-[12px] font-semibold transition-all border ${isSel ? 'text-white border-transparent shadow-glow' : 'bg-tertiary text-secondary-c border-strong hover:bg-hover-surface hover:text-primary-c'}`} style={isSel ? { background: t.color || '#0DA7CA' } : undefined}>
                          {t.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {options.providers.length > 1 && (
                <div>
                  <label className="block text-[12px] font-semibold text-secondary-c mb-2">Provider</label>
                  <select className={inputClass} value={providerId} onChange={(e) => setProviderId(e.target.value)}>
                    {options.providers.map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
                  </select>
                </div>
              )}
              {options.providers.length === 0 && <p className="text-[12px] text-warning">The clinic has no providers on the calendar yet — ask them to add one before booking.</p>}

              <div>
                <label className="block text-[12px] font-semibold text-secondary-c mb-2">Date</label>
                <input type="date" className={inputClass} min={minDate} value={date} onChange={(e) => setDate(e.target.value)} />
              </div>

              {date && (
                <div>
                  <label className="block text-[12px] font-semibold text-secondary-c mb-2">Open times at the clinic</label>
                  {slotsLoading ? (
                    <div className="grid grid-cols-4 gap-1.5">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-9 bg-tertiary rounded-lg animate-pulse" />)}</div>
                  ) : slots.length === 0 ? (
                    <p className="text-[12px] text-muted-c py-4 text-center bg-tertiary rounded-[10px]">No open times on this date</p>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                      {slots.map((s) => {
                        const isSel = slot?.starts_at === s.starts_at;
                        return (
                          <button key={s.starts_at} onClick={() => setSlot(s)} className={`py-2.5 rounded-lg text-[12px] font-semibold transition-all border ${isSel ? 'text-white border-transparent shadow-glow' : 'bg-tertiary text-secondary-c border-strong hover:bg-hover-surface hover:text-primary-c'}`} style={isSel ? { background: '#0DA7CA' } : undefined}>
                            {format(new Date(s.starts_at), 'h:mm a')}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {error && <div className="px-4 py-2.5 rounded-[10px] bg-danger-soft text-danger text-[12px] font-medium">{error}</div>}

          <div className="flex items-center justify-between gap-2.5 pt-4 border-t border-theme">
            <p className="text-[11px] text-muted-c">Confirm the time with {patientName} first — this books immediately.</p>
            <button onClick={book} disabled={!slot || submitting || !options} className="px-5 py-2.5 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50 flex-shrink-0 inline-flex items-center gap-1.5">
              {submitting ? <Loader2 size={13} className="animate-spin" /> : <CalendarCheck size={13} />} {submitting ? 'Booking…' : 'Book this time'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
